/**
 * Scroll Blocks — pin + cross-fade controller.
 *
 * Each `.rmh-scroll-blocks` instance pins its inner frame to the viewport
 * while the outer track scrolls past. When the controller engages it adds
 * `.is-enhanced` to the block root — the CSS keys all pin / opacity rules
 * off that class so the default (no-JS, single-child, mobile, reduced-motion,
 * editor) layout fails open as stacked normal flow. Active state is computed
 * from the track's scroll progress; we toggle `data-active="true|false"` on
 * each `.rmh-text-and-media` child to drive the CSS cross-fade.
 *
 * Skip conditions (no enhancement applied):
 *   - `prefers-reduced-motion: reduce`
 *   - block-editor context (Gutenberg iframe — no scroll-jacking in author UI)
 *   - <= 1 child (nothing to progress through)
 *   - viewport <= 767px (mobile stacks children, the controller re-engages on resize)
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const MOBILE_QUERY = '(max-width: 767px)';
const IN_EDITOR = typeof document !== 'undefined' && document.body && (
	document.body.classList.contains('block-editor-page') ||
	document.body.classList.contains('wp-admin')
);

function initBlock(block) {
	const track = block.querySelector('.rmh-scroll-blocks__track');
	const frame = block.querySelector('.rmh-scroll-blocks__frame');
	if (!track || !frame) return;

	const children = Array.from(frame.querySelectorAll('.rmh-text-and-media'));
	const count = children.length;
	if (count <= 1) return;

	// Set the count custom property so the track's min-height resolves correctly.
	block.style.setProperty('--rmh-scroll-blocks-count', String(count));

	let activeIndex = 0;
	let observing = false;
	let rafId = 0;
	let resizeRaf = 0;

	// Cache the sticky offset (header + admin-bar height). The frame pins at
	// `top: header + adminbar`, so progress should start counting only from the
	// moment track.top reaches that offset, not from when track.top hits 0.
	// `--rmh-adminbar-height` is declared on `body` (not `:root`), so we read
	// from the block itself which inherits both vars from the cascade. Cached
	// once and refreshed on resize to avoid a layout read on every scroll frame.
	const readStickyOffset = () => {
		const cs = getComputedStyle(block);
		const headerH = parseFloat(cs.getPropertyValue('--rmh-header-height')) || 0;
		const adminH = parseFloat(cs.getPropertyValue('--rmh-adminbar-height')) || 0;
		return headerH + adminH;
	};
	let stickyOffset = readStickyOffset();

	const computeProgress = () => {
		const rect = track.getBoundingClientRect();
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
		// Pin engages when track.top reaches `stickyOffset` (where the sticky
		// frame stops moving). Pin releases when track.bottom reaches viewport
		// bottom — i.e., track.top reaches `viewportHeight - rect.height`.
		// Total scrollable distance during pin: rect.height - viewportHeight + offset.
		const totalScroll = rect.height - viewportHeight + stickyOffset;
		if (totalScroll <= 0) return 0;
		const progress = (stickyOffset - rect.top) / totalScroll;
		return Math.max(0, Math.min(1, progress));
	};

	// Lightweight image preload: when child N becomes active, prefetch child N+1's image.
	const preloadedSet = new Set();
	function preloadNextImage(index) {
		const next = children[index + 1];
		if (!next || preloadedSet.has(index + 1)) return;
		preloadedSet.add(index + 1);
		const img = next.querySelector('img');
		if (!img || !img.currentSrc && !img.src) return;
		const link = document.createElement('link');
		link.rel = 'preload';
		link.as = 'image';
		link.href = img.currentSrc || img.src;
		document.head.appendChild(link);
	}

	const update = () => {
		rafId = 0;
		const progress = computeProgress();
		const next = Math.min(count - 1, Math.floor(progress * count));
		if (next !== activeIndex) {
			children[activeIndex].setAttribute('data-active', 'false');
			children[next].setAttribute('data-active', 'true');
			activeIndex = next;
			preloadNextImage(next);
		}
	};

	const onScroll = () => {
		if (rafId) return;
		rafId = requestAnimationFrame(update);
	};

	const onResize = () => {
		if (resizeRaf) return;
		resizeRaf = requestAnimationFrame(() => {
			resizeRaf = 0;
			stickyOffset = readStickyOffset();
			update();
		});
	};

	// Intersection observer gates the scroll listener — only run while the track
	// is anywhere near the viewport.
	const io = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting && !observing) {
				observing = true;
				window.addEventListener('scroll', onScroll, { passive: true });
				update();
			} else if (!entry.isIntersecting && observing) {
				observing = false;
				window.removeEventListener('scroll', onScroll);
				if (rafId) {
					cancelAnimationFrame(rafId);
					rafId = 0;
				}
			}
		});
	}, { rootMargin: '50% 0px 50% 0px' });

	const engage = () => {
		block.classList.add('is-enhanced');
		children.forEach((child, i) => {
			child.setAttribute('data-active', i === activeIndex ? 'true' : 'false');
		});
		preloadNextImage(activeIndex);
		io.observe(track);
		window.addEventListener('resize', onResize, { passive: true });
	};

	const disengage = () => {
		if (observing) {
			observing = false;
			window.removeEventListener('scroll', onScroll);
			if (rafId) {
				cancelAnimationFrame(rafId);
				rafId = 0;
			}
		}
		try { io.unobserve(track); } catch (e) {}
		window.removeEventListener('resize', onResize);
		if (resizeRaf) {
			cancelAnimationFrame(resizeRaf);
			resizeRaf = 0;
		}
		// Drop the enhancement class so default stacked CSS takes over, and clear
		// data-active flags so screen readers / authors see all children.
		block.classList.remove('is-enhanced');
		children.forEach((child) => child.removeAttribute('data-active'));
	};

	const mobileMql = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
	if (mobileMql && mobileMql.matches) {
		// Mobile: stay in default stacked layout. Don't engage.
	} else {
		engage();
	}

	if (mobileMql) {
		const onMobileChange = (e) => {
			if (e.matches) {
				disengage();
			} else {
				engage();
			}
		};
		if (typeof mobileMql.addEventListener === 'function') {
			mobileMql.addEventListener('change', onMobileChange);
		} else if (typeof mobileMql.addListener === 'function') {
			mobileMql.addListener(onMobileChange);
		}
	}
}

document.addEventListener('DOMContentLoaded', () => {
	if (IN_EDITOR) return;
	if (window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches) return;

	const blocks = document.querySelectorAll('.rmh-scroll-blocks:not(.is-editor-preview)');
	blocks.forEach(initBlock);
});
